import { jsPDF } from 'jspdf';

const TEAL = [26, 92, 90];
const RED = [255, 68, 68];
const LIGHT = [244, 247, 247];
const DARK = [45, 45, 45];
const GREY = [120, 120, 120];

const money = (v) =>
  'KES ' + (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Load an image (from /public) and return a PNG data URL
const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

export async function generateDebitNote(data) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentW = pageW - margin * 2;

  const logo = await loadImage('/definitelogo.png');

  // ---- Header band ----
  const headerH = 34;
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, pageW, headerH, 'F');
  // thin red accent under header
  doc.setFillColor(...RED);
  doc.rect(0, headerH, pageW, 1.5, 'F');

  // logo on a white rounded tile
  if (logo) {
    const tile = 22;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, 6, tile, tile, 3, 3, 'F');
    const pad = 2.5;
    const maxInner = tile - pad * 2;
    const ratio = Math.min(maxInner / logo.w, maxInner / logo.h);
    const dw = logo.w * ratio;
    const dh = logo.h * ratio;
    doc.addImage(logo.dataUrl, 'PNG', margin + (tile - dw) / 2, 6 + (tile - dh) / 2, dw, dh);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Definite Assurance', margin + 28, 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Marine Portal', margin + 28, 22);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(data.docTitle || 'DEBIT NOTE', pageW - margin, 16, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(data.date || new Date().toLocaleDateString(), pageW - margin, 22, { align: 'right' });

  let y = headerH + 9;

  // ---- Reference row ----
  doc.setFontSize(9);
  // Left reference (Debit Note number, or Policy No for policy documents)
  const refLabel = data.refLabel || 'Debit Note';
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  doc.text(`${refLabel}:`, margin, y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(String(data.debitNoteNo || '-'), margin + 24, y);
  // Proposal No (right)
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  doc.text('Proposal No:', pageW - margin - 60, y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(String(data.proposalNo || '-'), pageW - margin, y, { align: 'right' });
  y += 8;

  // ---- Section header helper ----
  const sectionHeader = (title, yy) => {
    doc.setFillColor(...TEAL);
    doc.rect(margin, yy, contentW, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text(title.toUpperCase(), margin + 3, yy + 4.8);
    return yy + 7;
  };

  const drawPairs = (pairs, x, startY, colW) => {
    let yy = startY;
    pairs.forEach(([label, value]) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...GREY);
      doc.text(String(label), x, yy);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...DARK);
      const lines = doc.splitTextToSize(String(value ?? '-'), colW);
      doc.text(lines, x, yy + 4);
      yy += 4 + lines.length * 4 + 2.5;
    });
    return yy;
  };

  // ---- Customer & Agent (two columns) ----
  y = sectionHeader('Customer & Agent Details', y);
  const boxTop = y + 2;
  const colW = (contentW - 8) / 2;
  const leftX = margin + 2;
  const rightX = margin + colW + 8;

  doc.setFillColor(...LIGHT);
  const custPairs = [
    ['Customer Name', data.client?.name],
    ['Identification No.', data.client?.id],
  ];
  const agentPairs = [
    ['Agent Name', data.agent?.name],
    ['Agent Code', data.agent?.code],
    ['Branch', data.agent?.branch],
  ];
  // background box sized to the taller column (agent has 3 rows)
  const boxH = 3 + agentPairs.length * 10.5 + 2;
  doc.roundedRect(margin, boxTop - 2, contentW, boxH, 2, 2, 'F');

  doc.setTextColor(...RED);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('CUSTOMER', leftX, boxTop + 2);
  doc.text('AGENT', rightX, boxTop + 2);

  drawPairs(custPairs, leftX, boxTop + 8, colW - 4);
  drawPairs(agentPairs, rightX, boxTop + 8, colW - 4);
  y = boxTop - 2 + boxH + 6;

  // ---- Cover Details ----
  y = sectionHeader('Cover Details', y);
  const coverPairs = [
    ['Cargo Type', data.cover?.cargoType],
    ['Freight Type', data.cover?.freightType],
    ['Cover Option', data.cover?.coverOption],
    ['Insured Item', data.cover?.item],
    ['Sum Insured', money(data.cover?.sumInsured)],
    ['Cover Period', (data.cover?.coverDays || 0) + ' days'],
    ['Commencement Date', data.cover?.commencement],
    ['Expiry Date', data.cover?.expiry],
  ];
  const coverBoxTop = y + 2;
  const coverRows = Math.ceil(coverPairs.length / 2);
  const coverBoxH = 3 + coverRows * 10.5 + 2;
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, coverBoxTop - 2, contentW, coverBoxH, 2, 2, 'F');

  // draw cover pairs in two columns
  let cy = coverBoxTop + 4;
  for (let i = 0; i < coverPairs.length; i += 2) {
    const rowY = cy;
    for (let idx = 0; idx < 2; idx++) {
      const pair = coverPairs[i + idx];
      if (!pair) continue;
      const x = idx === 0 ? leftX : rightX;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...GREY);
      doc.text(String(pair[0]), x, rowY);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...DARK);
      doc.text(String(pair[1] ?? '-'), x, rowY + 4);
    }
    cy += 10.5;
  }
  y = coverBoxTop - 2 + coverBoxH + 6;

  // ---- Premium Details (table) ----
  y = sectionHeader('Premium Details', y);
  const p = data.premium || {};
  const premiumRows = [
    ['Basic Premium', money(p.basicPremium)],
    ['Stamp Duty', money(p.stampDuty)],
    ['PHCF', money(p.PHCF)],
    ['Training Levy', money(p.trainingLevy)],
    ['Commission', money(p.commission)],
    ['Net Premium', money(p.netPremium)],
  ];
  let ty = y + 2;
  const rowH = 8;
  premiumRows.forEach((row, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(...LIGHT);
      doc.rect(margin, ty, contentW, rowH, 'F');
    }
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(row[0], margin + 4, ty + 5.5);
    doc.setFont('helvetica', 'bold');
    doc.text(row[1], pageW - margin - 4, ty + 5.5, { align: 'right' });
    ty += rowH;
  });
  // gross premium highlighted row
  doc.setFillColor(...TEAL);
  doc.rect(margin, ty, contentW, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('GROSS PREMIUM', margin + 4, ty + 6.5);
  doc.text(money(p.grossPremium), pageW - margin - 4, ty + 6.5, { align: 'right' });
  ty += 10;

  // ---- How to Make Payment ---- (hidden for policy documents)
  if (data.showPayment !== false) {
    let py = sectionHeader('How to Make Payment', ty + 8);
    const payTop = py + 2;
    const payH = 20;
    doc.setFillColor(...LIGHT);
    doc.roundedRect(margin, payTop, contentW, payH, 2, 2, 'F');
    const payItems = [
      ['Paybill Number', '200300'],
      ['Account Number', data.debitNoteNo || '-'],
      ['Amount', money(p.grossPremium)]
    ];
    const third = contentW / 3;
    for (let i = 0; i < payItems.length; i++) {
      const cx = margin + third * i + 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...GREY);
      doc.text(payItems[i][0], cx, payTop + 7);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...TEAL);
      doc.text(String(payItems[i][1]), cx, payTop + 14);
    }
  }

  // ---- Footer band ----
  const footerH = 26;
  const fy = pageH - footerH;
  doc.setFillColor(...TEAL);
  doc.rect(0, fy, pageW, footerH, 'F');
  doc.setFillColor(...RED);
  doc.rect(0, fy - 1.5, pageW, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('Definite Assurance Company Limited', pageW / 2, fy + 9, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('+254 708 247 247 / +254 788 247 247   |   customerservice@definiteassurance.com', pageW / 2, fy + 15, { align: 'center' });
  doc.text('ABSA Towers 1st Floor, Loita Street, Nairobi.', pageW / 2, fy + 20, { align: 'center' });

  const prefix = data.filePrefix || 'DebitNote';
  const fileName = `${prefix}-${data.proposalNo || data.debitNoteNo || 'proposal'}.pdf`.replace(/[/\\]/g, '-');
  doc.save(fileName);
}
